import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit, Trash2, Package, DollarSign, Image, Layers, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [artworks, setArtworks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const [artworkDialogOpen, setArtworkDialogOpen] = useState(false);
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [editingCollection, setEditingCollection] = useState(null);

  const imageInputRef = useRef(null);
  const downloadInputRef = useRef(null);
  const collectionImageInputRef = useRef(null);

  // Artwork form
  const [artworkForm, setArtworkForm] = useState({
    title: "",
    description: "",
    price: "",
    image_url: "",
    collection_id: "",
    season: "",
    year: new Date().getFullYear(),
    artist: "Family Collection",
    is_featured: false,
    download_url: "",
  });

  // Collection form
  const [collectionForm, setCollectionForm] = useState({
    name: "",
    description: "",
    season: "",
    year: new Date().getFullYear(),
    cover_image: "",
  });

  const fetchData = async () => {
    try {
      const [statsRes, artworksRes, collectionsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/artworks`),
        axios.get(`${API}/collections`),
        axios.get(`${API}/admin/orders`),
      ]);
      setStats(statsRes.data);
      setArtworks(artworksRes.data);
      setCollections(collectionsRes.data);
      setOrders(ordersRes.data);
    } catch (e) {
      console.error("Error fetching admin data:", e);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleImageUpload = async (file, type) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const url = response.data.url;
      
      if (type === "artwork_image") {
        setArtworkForm({ ...artworkForm, image_url: url });
      } else if (type === "artwork_download") {
        setArtworkForm({ ...artworkForm, download_url: url });
      } else if (type === "collection_image") {
        setCollectionForm({ ...collectionForm, cover_image: url });
      }
      
      toast.success("Image uploaded successfully!");
    } catch (e) {
      console.error("Upload error:", e);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
    }
  };

  const handleArtworkSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...artworkForm,
        price: parseFloat(artworkForm.price),
        year: parseInt(artworkForm.year),
      };

      if (editingArtwork) {
        await axios.put(`${API}/artworks/${editingArtwork.id}`, data);
        toast.success("Artwork updated");
      } else {
        await axios.post(`${API}/artworks`, data);
        toast.success("Artwork created");
      }

      setArtworkDialogOpen(false);
      resetArtworkForm();
      fetchData();
    } catch (e) {
      toast.error("Failed to save artwork");
    }
  };

  const handleCollectionSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...collectionForm,
        year: parseInt(collectionForm.year),
      };

      if (editingCollection) {
        await axios.put(`${API}/collections/${editingCollection.id}`, data);
        toast.success("Collection updated");
      } else {
        await axios.post(`${API}/collections`, data);
        toast.success("Collection created");
      }

      setCollectionDialogOpen(false);
      resetCollectionForm();
      fetchData();
    } catch (e) {
      toast.error("Failed to save collection");
    }
  };

  const handleDeleteArtwork = async (id) => {
    if (!confirm("Are you sure you want to delete this artwork?")) return;
    try {
      await axios.delete(`${API}/artworks/${id}`);
      toast.success("Artwork deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete artwork");
    }
  };

  const handleDeleteCollection = async (id) => {
    if (!confirm("Are you sure you want to delete this collection?")) return;
    try {
      await axios.delete(`${API}/collections/${id}`);
      toast.success("Collection deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete collection");
    }
  };

  const editArtwork = (artwork) => {
    setEditingArtwork(artwork);
    setArtworkForm({
      title: artwork.title,
      description: artwork.description,
      price: artwork.price.toString(),
      image_url: artwork.image_url,
      collection_id: artwork.collection_id,
      season: artwork.season,
      year: artwork.year,
      artist: artwork.artist,
      is_featured: artwork.is_featured,
      download_url: artwork.download_url || "",
    });
    setArtworkDialogOpen(true);
  };

  const editCollection = (collection) => {
    setEditingCollection(collection);
    setCollectionForm({
      name: collection.name,
      description: collection.description,
      season: collection.season,
      year: collection.year,
      cover_image: collection.cover_image,
    });
    setCollectionDialogOpen(true);
  };

  const resetArtworkForm = () => {
    setEditingArtwork(null);
    setArtworkForm({
      title: "",
      description: "",
      price: "",
      image_url: "",
      collection_id: "",
      season: "",
      year: new Date().getFullYear(),
      artist: "Family Collection",
      is_featured: false,
      download_url: "",
    });
  };

  const resetCollectionForm = () => {
    setEditingCollection(null);
    setCollectionForm({
      name: "",
      description: "",
      season: "",
      year: new Date().getFullYear(),
      cover_image: "",
    });
  };

  if (loading) {
    return (
      <div className="py-12 px-6 md:px-12 max-w-screen-2xl mx-auto">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted w-48" />
          <div className="grid grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-muted" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 md:px-12 lg:px-24 max-w-screen-2xl mx-auto" data-testid="admin-page">
      <h1 className="font-serif text-4xl md:text-5xl font-normal tracking-tight mb-8">Admin Dashboard</h1>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
        <div className="bg-muted/30 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Package className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Total Orders</span>
          </div>
          <p className="text-3xl font-medium" data-testid="stat-orders">{stats?.total_orders || 0}</p>
        </div>
        <div className="bg-muted/30 p-6">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Revenue</span>
          </div>
          <p className="text-3xl font-medium" data-testid="stat-revenue">${stats?.total_revenue?.toFixed(2) || "0.00"}</p>
        </div>
        <div className="bg-muted/30 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Image className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Artworks</span>
          </div>
          <p className="text-3xl font-medium" data-testid="stat-artworks">{stats?.total_artworks || 0}</p>
        </div>
        <div className="bg-muted/30 p-6">
          <div className="flex items-center gap-3 mb-2">
            <Layers className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Collections</span>
          </div>
          <p className="text-3xl font-medium" data-testid="stat-collections">{stats?.total_collections || 0}</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="artworks">
        <TabsList className="rounded-none mb-6">
          <TabsTrigger value="artworks" className="rounded-none" data-testid="artworks-tab">Artworks</TabsTrigger>
          <TabsTrigger value="collections" className="rounded-none" data-testid="collections-tab">Collections</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-none" data-testid="orders-tab">Orders</TabsTrigger>
        </TabsList>

        {/* Artworks Tab */}
        <TabsContent value="artworks">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-serif text-xl">Manage Artworks</h2>
            <Dialog open={artworkDialogOpen} onOpenChange={(open) => { setArtworkDialogOpen(open); if (!open) resetArtworkForm(); }}>
              <DialogTrigger asChild>
                <Button className="rounded-none gap-2" data-testid="add-artwork-btn">
                  <Plus className="w-4 h-4" />
                  Add Artwork
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingArtwork ? "Edit Artwork" : "Add New Artwork"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleArtworkSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={artworkForm.title}
                      onChange={(e) => setArtworkForm({ ...artworkForm, title: e.target.value })}
                      className="rounded-none mt-1"
                      required
                      data-testid="artwork-title-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={artworkForm.description}
                      onChange={(e) => setArtworkForm({ ...artworkForm, description: e.target.value })}
                      className="rounded-none mt-1"
                      required
                      data-testid="artwork-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={artworkForm.price}
                        onChange={(e) => setArtworkForm({ ...artworkForm, price: e.target.value })}
                        className="rounded-none mt-1"
                        required
                        data-testid="artwork-price-input"
                      />
                    </div>
                    <div>
                      <Label>Artist</Label>
                      <Input
                        value={artworkForm.artist}
                        onChange={(e) => setArtworkForm({ ...artworkForm, artist: e.target.value })}
                        className="rounded-none mt-1"
                        data-testid="artwork-artist-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Image</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={artworkForm.image_url}
                        onChange={(e) => setArtworkForm({ ...artworkForm, image_url: e.target.value })}
                        className="rounded-none flex-1"
                        placeholder="URL or upload an image"
                        data-testid="artwork-image-input"
                      />
                      <input
                        type="file"
                        ref={imageInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e.target.files[0], "artwork_image")}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      </Button>
                    </div>
                    {artworkForm.image_url && (
                      <img src={artworkForm.image_url} alt="Preview" className="mt-2 w-20 h-20 object-cover" />
                    )}
                  </div>
                  <div>
                    <Label>Download File</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={artworkForm.download_url}
                        onChange={(e) => setArtworkForm({ ...artworkForm, download_url: e.target.value })}
                        className="rounded-none flex-1"
                        placeholder="URL or upload download file"
                        data-testid="artwork-download-input"
                      />
                      <input
                        type="file"
                        ref={downloadInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e.target.files[0], "artwork_download")}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none"
                        onClick={() => downloadInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Season</Label>
                      <Select value={artworkForm.season} onValueChange={(v) => setArtworkForm({ ...artworkForm, season: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="artwork-season-select">
                          <SelectValue placeholder="Select season" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="spring">Spring</SelectItem>
                          <SelectItem value="summer">Summer</SelectItem>
                          <SelectItem value="fall">Fall</SelectItem>
                          <SelectItem value="winter">Winter</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Year</Label>
                      <Input
                        type="number"
                        value={artworkForm.year}
                        onChange={(e) => setArtworkForm({ ...artworkForm, year: parseInt(e.target.value) })}
                        className="rounded-none mt-1"
                        required
                        data-testid="artwork-year-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Collection</Label>
                    <Select value={artworkForm.collection_id} onValueChange={(v) => setArtworkForm({ ...artworkForm, collection_id: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="artwork-collection-select">
                        <SelectValue placeholder="Select collection" />
                      </SelectTrigger>
                      <SelectContent>
                        {collections.map((col) => (
                          <SelectItem key={col.id} value={col.id}>{col.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_featured"
                      checked={artworkForm.is_featured}
                      onChange={(e) => setArtworkForm({ ...artworkForm, is_featured: e.target.checked })}
                      data-testid="artwork-featured-checkbox"
                    />
                    <Label htmlFor="is_featured">Featured Artwork</Label>
                  </div>
                  <Button type="submit" className="w-full rounded-none" data-testid="artwork-submit-btn">
                    {editingArtwork ? "Update Artwork" : "Create Artwork"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Image</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Season</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {artworks.map((artwork) => (
                <TableRow key={artwork.id} data-testid={`artwork-row-${artwork.id}`}>
                  <TableCell>
                    <img src={artwork.image_url} alt={artwork.title} className="w-12 h-16 object-cover" />
                  </TableCell>
                  <TableCell className="font-medium">{artwork.title}</TableCell>
                  <TableCell>${artwork.price.toFixed(2)}</TableCell>
                  <TableCell className="capitalize">{artwork.season}</TableCell>
                  <TableCell>{artwork.is_featured ? "Yes" : "No"}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => editArtwork(artwork)} data-testid={`edit-artwork-${artwork.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteArtwork(artwork.id)} data-testid={`delete-artwork-${artwork.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        {/* Collections Tab */}
        <TabsContent value="collections">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-serif text-xl">Manage Collections</h2>
            <Dialog open={collectionDialogOpen} onOpenChange={(open) => { setCollectionDialogOpen(open); if (!open) resetCollectionForm(); }}>
              <DialogTrigger asChild>
                <Button className="rounded-none gap-2" data-testid="add-collection-btn">
                  <Plus className="w-4 h-4" />
                  Add Collection
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editingCollection ? "Edit Collection" : "Add New Collection"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCollectionSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={collectionForm.name}
                      onChange={(e) => setCollectionForm({ ...collectionForm, name: e.target.value })}
                      className="rounded-none mt-1"
                      required
                      data-testid="collection-name-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={collectionForm.description}
                      onChange={(e) => setCollectionForm({ ...collectionForm, description: e.target.value })}
                      className="rounded-none mt-1"
                      required
                      data-testid="collection-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Season</Label>
                      <Select value={collectionForm.season} onValueChange={(v) => setCollectionForm({ ...collectionForm, season: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="collection-season-select">
                          <SelectValue placeholder="Select season" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="spring">Spring</SelectItem>
                          <SelectItem value="summer">Summer</SelectItem>
                          <SelectItem value="fall">Fall</SelectItem>
                          <SelectItem value="winter">Winter</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Year</Label>
                      <Input
                        type="number"
                        value={collectionForm.year}
                        onChange={(e) => setCollectionForm({ ...collectionForm, year: parseInt(e.target.value) })}
                        className="rounded-none mt-1"
                        required
                        data-testid="collection-year-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Cover Image</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        value={collectionForm.cover_image}
                        onChange={(e) => setCollectionForm({ ...collectionForm, cover_image: e.target.value })}
                        className="rounded-none flex-1"
                        placeholder="URL or upload an image"
                        data-testid="collection-image-input"
                      />
                      <input
                        type="file"
                        ref={collectionImageInputRef}
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e.target.files[0], "collection_image")}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-none"
                        onClick={() => collectionImageInputRef.current?.click()}
                        disabled={uploading}
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      </Button>
                    </div>
                    {collectionForm.cover_image && (
                      <img src={collectionForm.cover_image} alt="Preview" className="mt-2 w-20 h-20 object-cover" />
                    )}
                  </div>
                  <Button type="submit" className="w-full rounded-none" data-testid="collection-submit-btn">
                    {editingCollection ? "Update Collection" : "Create Collection"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cover</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Season</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {collections.map((collection) => (
                <TableRow key={collection.id} data-testid={`collection-row-${collection.id}`}>
                  <TableCell>
                    <img src={collection.cover_image} alt={collection.name} className="w-16 h-20 object-cover" />
                  </TableCell>
                  <TableCell className="font-medium">{collection.name}</TableCell>
                  <TableCell className="capitalize">{collection.season}</TableCell>
                  <TableCell>{collection.year}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => editCollection(collection)} data-testid={`edit-collection-${collection.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteCollection(collection.id)} data-testid={`delete-collection-${collection.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders">
          <h2 className="font-serif text-xl mb-6">Recent Orders</h2>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                  <TableCell className="font-mono text-xs">{order.id.slice(0, 8)}...</TableCell>
                  <TableCell>{order.email}</TableCell>
                  <TableCell>{order.items.length} item(s)</TableCell>
                  <TableCell>${order.total.toFixed(2)}</TableCell>
                  <TableCell className="capitalize">{order.payment_method}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 text-xs ${order.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                      {order.payment_status}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No orders yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPage;
